<template>
  <div class="gap-6 overflow-hidden lg:grid lg:grid-cols-2 xl:gap-16">
    <div class="space-y-4 lg:space-y-8">
      <button
          :class="['flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!', {'bordered': mode === 'contact'}]"
          @click="mode='contact'"
      >
        <NuxtPicture
            src="/images/icons/icon-envelope.svg"
            alt="Kontaktformular"
        />
        <span>Kontaktformular</span>
      </button>
      <button
          :class="['flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!', {'bordered': mode === 'booking'}]"
          @click="mode='booking'"
      >
        <IconPersonWaving/>
        <span>Beratungstermin buchen</span>
      </button>
      <button
          class="flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!"
          @click="placeCall()"
      >
        <NuxtPicture
            src="/images/icons/icon-phone.svg"
            alt="Direktanruf"
        />
        <span>Direktanruf</span>
      </button>
      <button
          :class="['flex! gap-4 w-full py-2! lg:py-4! items-center font-bold scale-100!', {'bordered': mode === 'callback'}]"
          @click="mode='callback'"
      >
        <NuxtPicture
            src="/images/icons/icon-recall-service.svg"
            alt="Rückrufservice"
        />
        <span>Rückrufservice</span>
      </button>
      <IconStickFigurePhone class="hidden lg:block"/>
    </div>

    <form
        v-if="mode !== 'booking'"
        class="contact-form space-y-4 py-4 text-xs lg:space-y-8 lg:py-0 lg:text-base"
        @submit.prevent="sendMessage"
    >
      <div class="space-y-4">
        <UFormField
            label="Ihre E-Mail-Adresse"
            :required="mode === 'contact'"
        >
          <UInput
              v-model="email"
              placeholder="E-Mail-Adresse"
              type="email"
              class="w-full"
              :class="(mode === 'contact' && errorEmail) ? 'border-2 border-error-600 rounded' : ''"
          />
        </UFormField>
        <UFormField
            label="Ihre Telefonnummer"
            :required="mode === 'callback'"
        >
          <UInput
              v-model="phone"
              placeholder="Telefonnummer"
              type="tel"
              class="w-full"
              :class="(mode === 'callback' && errorPhone) ? 'border-2 border-error-600 rounded' : ''"
          />
        </UFormField>
        <UFormField
            label="Ihre Postleitzahl"
        >
          <UInput
              v-model="zip"
              placeholder="Postleitzahl"
              type="number"
              class="w-full"
          />
        </UFormField>
        <UFormField
            label="Ihre Nachricht an uns"
            required
        >
          <UTextarea
              v-model="message"
              :rows="6"
              placeholder="Ihre Nachricht"
              class="w-full"
              :class="errorMessage ? 'border-2 border-error-600 rounded' : ''"
          />
        </UFormField>
        <UCheckbox
            v-model="privacy"
            required
            style="padding-inline: 0 !important; padding-block: 0 !important;"
            :class="errorPrivacy ? 'border-2 border-error-600 rounded' : ''"
        >
          <template #label>
            Ich habe die <a
              href="/Datenschutz/"
              target="_blank"
              class="text-primary"
          >Datenschutzhinweise</a> zur Kenntnis genommen.
          </template>
        </UCheckbox>
      </div>

      <div class="block text-xs">
        Bei den mit einem Sternchen (*) markierten Feldern handelt es sich um Pflichtfelder!
      </div>

      <button
          class="w-full bg-primary text-white"
          type="submit"
      >
        Nachricht absenden
      </button>
    </form>
    <FormBooking
        v-if="mode === 'booking'"
        @sent="sendBooking"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watchEffect} from 'vue';
import {z} from 'zod';
import {useForm, useField} from 'vee-validate';
import {toTypedSchema} from '@vee-validate/zod';
import IconStickFigurePhone from '~/components/Icons/IconStickFigurePhone.vue';
import IconPersonWaving from '~/components/Icons/IconPersonWaving.vue';

const {public: pub} = useRuntimeConfig();
const props = defineProps<{ article: any }>();
const emit = defineEmits<{(e: 'close'): void
  (e: 'sent', payload: { success: boolean }): void
}>();

const mode = ref<'contact' | 'booking' | 'callback'>('contact');
const zip = ref('');
const email = ref('');
const phone = ref('');
const message = ref('');
const privacy = ref(false);
const errorEmail = ref(false);
const errorPhone = ref(false);
const errorMessage = ref(false);
const errorPrivacy = ref(false);

function placeCall() {
    document.location.assign('tel:+49295576061830');
}

watchEffect(() => {
    if (!props.article?.articleNumber) return;
});

function validation() {
    if (mode.value === 'contact' && email.value === '') {
        errorEmail.value = true;
        return false;
    }
    errorEmail.value = false;

    if (mode.value === 'callback' && phone.value === '') {
        errorPhone.value = true;
        return false;
    }
    errorPhone.value = false;

    if (!message.value) {
        errorMessage.value = true;
        return false;
    }
    errorMessage.value = false;

    if (!privacy.value) {
        errorPrivacy.value = true;
        return false;
    }
    errorPrivacy.value = false;

    return true;
}

async function sendMessage() {
    if (validation()) {
        const response = await $fetch<{ sent: boolean }>(`${pub.apiUrl}/api/contact`, {
            method: 'POST',
            body: {
                mode: mode.value,
                zip: zip.value,
                email: email.value,
                phone: phone.value,
                message: message.value,
                article: `${props.article.articleNumber} - ${props.article.name} ${props.article.name2}`,
            },
        });

        emit('sent', {success: !!response?.sent});
    }
}

function sendBooking(e) {
    emit('sent', {success: true});
}
</script>
